<template>
    <div>
        <table v-if="customerContactDetails.length > 0" class="table condensed striped">
            <tbody>
                <tr v-for="contactDetail in customerContactDetails">
                    <td class="w-1/3">
                        {{ contactDetail.label }}
                    </td>
                    <td class="w-1/2">
                        <font-awesome-icon
                            v-if="hasWarning(contactDetail)"
                            icon="exclamation-triangle"
                            class="text-warning"
                            title="Vänligen kontrollera att denna kontaktuppgift är rättstavad."
                        />
                        {{ contactDetail.value }}
                    </td>
                    <td class="actions">
                        <div>
                            <button
                                class="btn-circle is-small"
                                @click="editDetail(contactDetail)"
                            >
                                <font-awesome-icon icon="pencil" size="sm" />
                            </button>
                            <button
                                v-if="canBeDeleted(contactDetail)"
                                class="btn-circle is-small is-danger"
                                @click="deleteDetail(contactDetail)"
                            >
                                <font-awesome-icon icon="minus" class="text-xxs" />
                            </button>
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>
        <div v-else>
            <p>Denna kund har inga kontaktuppgifter.</p>
        </div>

        <div class="text-right">
            <a
                class="btn btn-md is-ghost mt-lg"
                @click="$modal.show('add-contact-detail')"
            >
                Lägg till
            </a>
        </div>

        <adaptive-form-modal
            name="add-contact-detail"
            method="post"
            primary-button-text="Spara kontaktinformation"
            primary-button-class="is-primary"
            title="Lägg till kontaktinformation"
            :action="actionRoute"
            :callback="storeContactDetailCallback"
        >
            <select-input
                v-model="selectedType"
                name="type"
                :options="contactTypes"
            >
                Typ
            </select-input>
            <div class="form-group form-group-floating-label">
                <input
                    id="label"
                    v-model="label"
                    class="form-input"
                    name="label"
                    required
                >
                <label for="label" class="floating-label">
                    Etikett
                </label>
            </div>
            <div class="form-group form-group-floating-label">
                <input
                    id="value"
                    class="form-input"
                    name="value"
                    required
                >
                <label for="value" class="floating-label">
                    Värde
                </label>
            </div>
        </adaptive-form-modal>

        <adaptive-form-modal
            name="edit-contact-detail"
            method="patch"
            primary-button-text="Uppdatera kontaktinformation"
            primary-button-class="is-primary"
            title="Redigera kontaktinformation"
            :action="updateActionRoute"
            :callback="updateDetailCallback"
        >
            <select-input
                v-model="selectedDetail.type"
                name="type"
                :options="contactTypes"
            >
                Typ
            </select-input>
            <div class="form-group form-group-floating-label">
                <input
                    id="edit_label"
                    v-model="selectedDetail.label"
                    class="form-input"
                    name="label"
                    required
                >
                <label for="edit_label" class="floating-label">
                    Etikett
                </label>
            </div>
            <div class="form-group form-group-floating-label">
                <input
                    id="edit_value"
                    v-model="selectedDetail.value"
                    class="form-input"
                    name="value"
                    required
                >
                <label for="edit_value" class="floating-label">
                    Värde
                </label>
            </div>
        </adaptive-form-modal>
    </div>
</template>

<script>
    import AdaptiveFormModal from '../Base/Modals/AdaptiveFormModal.vue';

    export default {
        components: {
            AdaptiveFormModal,
        },

        props: {
            customer: {
                required: true,
                type: Object,
            },
            contactDetails: {
                required: true,
                type: Array,
            },
            contactTypes: {
                required: true,
                type: Object,
            },
            countryCode: {
                required: false,
                type: String,
                default: 'none',
            },
        },

        data() {
            return {
                customerContactDetails: [],
                label: '',
                selectedType: null,
                selectedDetail: {},
            };
        },

        computed: {
            actionRoute() {
                return route('api.customers.contact-details.store', this.customer);
            },
            updateActionRoute() {
                if (! this.selectedDetail.id) return '';

                return route('api.customers.contact-details.update', [this.customer, this.selectedDetail]);
            },
        },

        watch: {
            selectedType(newType) {
                this.label = this.contactTypes[newType];
            },
        },

        created() {
            // Use state since we don't want to update a prop
            this.customerContactDetails = this.contactDetails;
            this.selectedType = Object.keys(this.contactTypes)[0];
        },

        methods: {
            // A contact type can only be deleted if multiple of the same type exists
            canBeDeleted(currentDetail) {
                return this.customerContactDetails
                    .filter(detail => detail.type === currentDetail.type)
                    .length > 1;
            },

            storeContactDetailCallback(response) {
                this.customerContactDetails.push(response.data.contactDetail);

                this.$modal.hide('add-contact-detail');
            },

            deleteDetail(currentDetail) {
                axios.delete(route('api.customers.contact-details.destroy', [this.customer, currentDetail]))
                    .then(() => {
                        const index = this.customerContactDetails.findIndex(detail => detail.id === currentDetail.id);
                        if (index >= 0) {
                            this.customerContactDetails.splice(index, 1);
                        }
                    })
                    .catch(error => {
                        flash_error('Kunde inte ta bort kontaktinformationen');
                    });
            },

            editDetail(detail) {
                const { id, type, label, value } = detail;
                this.selectedDetail = {
                    id,
                    type,
                    label,
                    value,
                };

                this.$modal.show('edit-contact-detail');
            },

            updateDetailCallback({ data }) {
                this.customerContactDetails = data.contactDetails;

                this.$modal.hide('edit-contact-detail');

                flash_success('Kontaktinformationen uppdaterades');
            },

            hasWarning(contactDetail) {
                switch (contactDetail.type) {
                    case 'email':
                        return this.hasWarningForEmail(contactDetail);
                    case 'mobile_phone_number':
                        return this.hasWarningForMobile(contactDetail);
                    default:
                        return false;
                }
            },

            hasWarningForEmail(contactDetail) {
                if (! contactDetail.value) return true;

                const val = contactDetail.value;

                if (val.indexOf('.') <= 0) return true;

                const indexOfAtSign = val.indexOf('@');

                if (indexOfAtSign <= 0) return true;

                const lastIndexOfDot = val.lastIndexOf('.');

                if (lastIndexOfDot < indexOfAtSign) return true;
                if (lastIndexOfDot === indexOfAtSign + 1) return true;
                if (lastIndexOfDot === val.length - 1) return true;

                return false;
            },

            hasWarningForMobile(contactDetail) {
                if (this.countryCode !== 'se') return false;

                if (! contactDetail.value) return true;

                const val = contactDetail.value;

                if (val[0] === '+' && val.length !== 12) return true;
                if (val[0] === '0' && val.length !== 10) return true;

                return false;
            },
        },
    };
</script>
